import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import VariableInput from '../components/VariableInput';
import Scheduler from '../components/Scheduler';
import KeyValueInput from '../components/KeyValueInput';
import './CrawlingRequestForm.css';

const CrawlingRequestForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      urlTemplate: '',
      method: 'GET',
      responseParser: '',
      enabled: true
    }
  });

  const [variables, setVariables] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [bodyParams, setBodyParams] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [previewResponse, setPreviewResponse] = useState('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const watchedUrlTemplate = watch('urlTemplate');
  const watchedMethod = watch('method');
  const watchedResponseParser = watch('responseParser');

  // 편집 모드일 때 데이터 로드
  useEffect(() => {
    if (isEdit) {
      // TODO: API에서 기존 데이터 로드
      console.log('Loading existing request:', id);
    }
  }, [isEdit, id]);

  const onSubmit = async (data) => {
    try {
      const requestData = {
        ...data,
        variables,
        headers,
        bodyParams: watchedMethod !== 'GET' ? bodyParams : [],
        schedule
      };

      console.log('Submitting crawling request:', requestData);

      // TODO: API 호출
      // if (isEdit) {
      //   await updateCrawlingRequest(id, requestData);
      // } else {
      //   await createCrawlingRequest(requestData);
      // }

      // 성공 시 대시보드로 이동
      navigate('/crawling');
    } catch (error) {
      console.error('Failed to save crawling request:', error);
    }
  };

  const handleTestUrl = async () => {
    if (!watchedUrlTemplate) return;

    try {
      // URL 템플릿에서 변수를 실제 값으로 치환하여 테스트
      let testUrl = watchedUrlTemplate;
      variables.forEach(variable => {
        const regex = new RegExp(`{{${variable.name}}}`, 'g');
        testUrl = testUrl.replace(regex, variable.defaultValue || `[${variable.name}]`);
      });

      console.log('Testing URL:', testUrl);
      // TODO: 실제 URL 테스트 구현
      alert(`테스트 URL: ${testUrl}`);
    } catch (error) {
      console.error('URL test failed:', error);
    }
  };

  const handlePreviewResponse = async () => {
    if (!watchedResponseParser) return;

    setIsPreviewLoading(true);
    try {
      // 모의 응답 데이터
      const mockResponse = {
        status: 200,
        data: {
          items: [
            { id: 1, title: "Sample Item 1", price: 1000, category: "electronics" },
            { id: 2, title: "Sample Item 2", price: 2000, category: "books" }
          ],
          pagination: { page: 1, total: 100 },
          timestamp: new Date().toISOString()
        }
      };

      // Mustache 템플릿 처리 시뮬레이션
      let preview = watchedResponseParser;

      // 간단한 변수 치환 예시
      preview = preview.replace(/\{\{#items\}\}/g, '[\n');
      preview = preview.replace(/\{\{\/items\}\}/g, '\n]');
      preview = preview.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        return `"${key}": "sample_value"`;
      });

      setPreviewResponse(JSON.stringify(mockResponse, null, 2));
    } catch (error) {
      console.error('Preview failed:', error);
      setPreviewResponse('미리보기 실패: ' + error.message);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const validateUrl = (url) => {
    if (!url) return 'URL을 입력해주세요.';

    try {
      // 변수 치환 후 URL 검증
      let testUrl = url;
      variables.forEach(variable => {
        const regex = new RegExp(`{{${variable.name}}}`, 'g');
        testUrl = testUrl.replace(regex, variable.defaultValue || 'test');
      });

      new URL(testUrl);
      return true;
    } catch {
      return 'URL 형식이 올바르지 않습니다.';
    }
  };

  return (
    <div className="form-container">
      <div className="form-wrapper">
        {/* Header */}
        <div className="form-header">
          <div className="header-content">
            <div>
              <h2 className="form-title">
                {isEdit ? '크롤링 요청 수정' : '새 크롤링 요청'}
              </h2>
              <p className="form-subtitle">
                웹 크롤링 요청을 생성하고 스케줄을 설정하세요.
              </p>
            </div>
            <button
              onClick={() => navigate('/crawling')}
              className="close-btn"
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* 기본 정보 */}
          <div className="form-section">
            <h3 className="section-title">기본 정보</h3>
            <div className="form-grid two-cols">
              <div className="form-group">
                <label className="form-label required">요청명</label>
                <input
                  {...register('name', { required: '요청명을 입력해주세요.' })}
                  type="text"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="예: 뉴스 크롤링"
                />
                {errors.name && (
                  <p className="error-message">{errors.name.message}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">HTTP 메서드</label>
                <select
                  {...register('method')}
                  className="form-select"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label className="form-label">설명</label>
                <textarea
                  {...register('description')}
                  className="form-textarea"
                  rows="3"
                  placeholder="크롤링 요청에 대한 설명을 입력하세요..."
                />
              </div>
            </div>
          </div>

          {/* URL 템플릿 */}
          <div className="form-section">
            <h3 className="section-title">URL 템플릿</h3>
            <div className="form-group">
              <label className="form-label required">URL 템플릿</label>
              <div className="url-group">
                <div className="url-input">
                  <input
                    {...register('urlTemplate', {
                      required: 'URL을 입력해주세요.',
                      validate: validateUrl
                    })}
                    type="url"
                    className={`form-input ${errors.urlTemplate ? 'error' : ''}`}
                    placeholder="https://api.example.com/{{category}}/{{page}}"
                  />
                  {errors.urlTemplate && (
                    <p className="error-message">{errors.urlTemplate.message}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleTestUrl}
                  className="test-btn"
                >
                  URL 테스트
                </button>
              </div>
              <p className="help-text">
                변수는 {'{{변수명}}'} 형태로 사용하세요. 예: https://api.example.com/{'{{category}}'}/{'{{page}}'}
              </p>
            </div>
          </div>

          {/* 변수 관리 */}
          <div className="form-section">
            <VariableInput variables={variables} onChange={setVariables} />
          </div>

          {/* HTTP 헤더 */}
          <div className="form-section">
            <KeyValueInput
              title="HTTP 헤더"
              description="요청에 포함할 HTTP 헤더를 설정하세요."
              items={headers}
              onChange={setHeaders}
              keyPlaceholder="헤더명 (예: Authorization)"
              valuePlaceholder="값 (예: Bearer {{token}})"
            />
          </div>

          {/* HTTP 바디 (POST/PUT/DELETE일 때만) */}
          {watchedMethod !== 'GET' && (
            <div className="form-section">
              <KeyValueInput
                title="HTTP 바디"
                description="요청 바디에 포함할 파라미터를 설정하세요."
                items={bodyParams}
                onChange={setBodyParams}
                keyPlaceholder="파라미터명"
                valuePlaceholder="값"
                allowMultiline={true}
              />
            </div>
          )}

          {/* 응답 파싱 템플릿 */}
          <div className="form-section">
            <h3 className="section-title">응답 파싱</h3>
            <div className="form-group">
              <label className="form-label">파싱 템플릿 (Mustache)</label>
              <div className="parser-group">
                <textarea
                  {...register('responseParser')}
                  className="parser-textarea form-textarea"
                  placeholder={`{
  "items": [
    {{#items}}
    {
      "title": "{{title}}",
      "price": "{{price}}"
    }{{^last}},{{/last}}
    {{/items}}
  ]
}`}
                />
                <button
                  type="button"
                  onClick={handlePreviewResponse}
                  disabled={isPreviewLoading}
                  className="preview-btn"
                >
                  {isPreviewLoading ? '처리중...' : '미리보기'}
                </button>
              </div>
              <p className="help-text">
                Mustache 문법을 사용하여 응답 데이터를 파싱하세요.
              </p>
            </div>

            {previewResponse && (
              <div className="preview-result">
                <h4 className="preview-title">미리보기 결과</h4>
                <pre className="preview-content">{previewResponse}</pre>
              </div>
            )}
          </div>

          {/* 스케줄러 */}
          <div className="form-section">
            <Scheduler schedule={schedule} onChange={setSchedule} />
          </div>

          {/* 설정 */}
          <div className="form-section">
            <h3 className="section-title">설정</h3>
            <div className="checkbox-group">
              <input
                {...register('enabled')}
                type="checkbox"
                className="form-checkbox"
              />
              <label className="checkbox-label">
                크롤링 요청 활성화
              </label>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/crawling')}
              className="btn btn-secondary"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <>
                  <div className="loading-spinner-inline"></div>
                  저장 중...
                </>
              ) : (
                isEdit ? '수정' : '생성'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrawlingRequestForm;